import type { ReactElement } from "react"
import { PriceVariantType } from "@onestore/hel/dist/components/molecules/PriceMol/priceMol.types"
import { PeriodInfo } from "@core/period-info"
import type { CloudBluePlan } from "@gatsby-plugin-definitions/fragments/CloudBluePlan"
import DetailedPrice from "@gatsby-plugin-generic-page/components/DetailedPrice"
import DetailedStaticPrice from "@gatsby-plugin-generic-page/components/DetailedStaticPrice"
import { getStaticPrices } from "@gatsby-plugin-generic-page/helpers/staticPrices"
import { SquidexReferenceType } from "@gatsby-plugin-generic-page/types/referenceType"
import { typeMatches } from "@onestore-graphql"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import type { StaticPrice } from "~/fragments/staticPrice"
import isEmpty from "~/lib/isEmpty"
import type { CloudBlueDomain } from "~/types/squidex"

type Price = CloudBluePlan | CloudBlueDomain | StaticPrice

type MarketplaceMediaCardPriceProps = {
  price: Price[]
}

export default function MarketplaceMediaCardPrice({
  price,
}: MarketplaceMediaCardPriceProps): ReactElement<MarketplaceMediaCardPriceProps> | null {
  const { currentPriceType } = usePriceTypeContext()

  if (isEmpty(price)) return null

  if (
    typeMatches(price[0].__typename, SquidexReferenceType.CLOUDBLUE_PLAN) ||
    typeMatches(price[0].__typename, SquidexReferenceType.CLOUDBLUE_DOMAIN)
  ) {
    const { flatData } = price[0] as CloudBluePlan | CloudBlueDomain
    const defaultPeriod = flatData.periods.find((period) => period.default)

    if (!defaultPeriod) {
      return null
    }

    return (
      <DetailedPrice
        variant={{ type: PriceVariantType.MEDIUM, align: "left" }}
        layoutVariant="default"
        periodInfo={new PeriodInfo(defaultPeriod)}
        priceInfo={flatData.priceInfo}
      />
    )
  }

  if (typeMatches(price[0].__typename, SquidexReferenceType.STATIC_PRICE)) {
    const staticPricesData = price as StaticPrice[]
    const staticPrices = getStaticPrices(staticPricesData, currentPriceType)
    const period = Object.keys(staticPrices)[0]

    return (
      <DetailedStaticPrice
        variant={{ type: PriceVariantType.MEDIUM, align: "left" }}
        layoutVariant="default"
        staticPrices={staticPricesData}
        period={period}
      />
    )
  }

  return null
}
